import { CoreUser } from '@densityco/lib-api-types';
import { stringToBoolean } from '@densityco/lib-common-helpers';

export const PERMISSION_CODES = {
  // Permission given to Density superusers to impersonate other organizations. Used on the Internal Admin.
  impersonate: 'impersonate',
  // Permission to list organizations in the Accounts API.
  listOrganizations: 'list_organizations',
  // Permissions given to users to allow them to write to the Core API.
  coreWrite: 'core_write',
  // Permissions given to users to allow them to read from the Core API.
  coreRead: 'core_read',
  // Permission given to demo users: readonly, can interact with Dashboard.
  demo: 'demo',
  // Permission to allow a user to manage owner-level users
  ownerUserManage: 'owner_user_manage',
  // Permission to allow a user to manage admin-level users
  adminUserManage: 'admin_user_manage',
  // Permission to allow a user to manage readonly-level users
  readonlyUserManage: 'readonly_user_manage',
  // Permission to allow a user to access developer tools
  developerToolsManage: 'developer_tools_manage',
  // Permission to allow a user to manage global settings (for their org)
  globalSettingsManage: 'global_settings_manage',
  // Permission to allow a user to list or view sensors
  sensorsList: 'sensors_list',
};

export const ROLE_INFO: {
  [key: string]: { label: string; description: string };
} = {
  owner: {
    label: 'Owner',
    description: 'Full access and all permissions, including developer tools.',
  },
  admin: {
    label: 'Admin',
    description: 'Edit spaces and users. Cannot access developer tools.',
  },
  readonly: {
    label: 'Read-only',
    description: 'Cannot make changes to spaces or users.',
  },
  service: {
    label: 'Service',
    description: 'Service user.',
  },
};

export default function can(user: CoreUser | undefined, permission: string) {
  return (user?.permissions || []).indexOf(permission) > -1;
}

export function getManageableRoles(user: CoreUser) {
  const roles: string[] = [];
  if (can(user, PERMISSION_CODES.ownerUserManage)) {
    roles.push('owner');
  }
  if (can(user, PERMISSION_CODES.adminUserManage)) {
    roles.push('admin');
  }
  if (can(user, PERMISSION_CODES.readonlyUserManage)) {
    roles.push('readonly');
  }
  return roles;
}

// Helpers for displaying navbar features based on user
export function showOpenArea(_user: CoreUser | undefined) {
  return true;
}

export function showPortfolio(user: CoreUser | undefined) {
  return stringToBoolean(user?.organization.settings.portfolio_enabled || false);
}

export function showAdminMenu(user: CoreUser | undefined) {
  return (
    can(user, PERMISSION_CODES.developerToolsManage) ||
    can(user, PERMISSION_CODES.ownerUserManage) ||
    can(user, PERMISSION_CODES.adminUserManage) ||
    can(user, PERMISSION_CODES.readonlyUserManage)
  );
}

export function showDevTools(user: CoreUser | undefined) {
  return can(user, PERMISSION_CODES.developerToolsManage);
}

export function showOrganizationSettings(user: CoreUser | undefined) {
  return can(user, PERMISSION_CODES.globalSettingsManage);
}

export function showSensorList(user: CoreUser | undefined) {
  return can(user, PERMISSION_CODES.sensorsList);
}
